/**
 * User Admin V2 - Modern User Management Interface
 *
 * Provides comprehensive user management functionality for APP_ADMIN and CLIENT_ADMIN roles:
 * - APP_ADMIN: Can select any client and manage users across all clients
 * - CLIENT_ADMIN: Automatically scoped to their own client's users
 * - Full CRUD operations: Create, view, edit, and deactivate users
 * - Role management: Assign GroupRole and optional role (grm client only)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';

// Layout
import DashboardLayout from 'layouts/DashboardLayout';
import DashboardNavbar from 'components/RDNavbars/DashboardNavbar';

// Components
import AdminButton from 'components/common/AdminButton';

// Components
import ClientSelector from '../../../../components/common/admin/ClientSelector';
import UserTable from './components/UserTable';
import UserForm from './components/UserForm';
import DeactivateUserDialog from './components/DeactivateUserDialog';

// Hooks
import { useUser } from '../../../../hooks/useUser';
import { useUserPermissions } from './hooks/useUserPermissions';
import { useAdminContext } from '../../../../context/AdminContext';
import { useUserData } from './hooks/useUserData';

// Constants
import { GROUP_ROLES } from './constants';

/**
 * UserAdminV2 Component
 *
 * Main entry point for the User Admin V2 page. Handles permission-based
 * rendering and provides the overall layout structure.
 */
export default function UserAdminV2() {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');

  // Check user permissions
  const { isAppAdmin, isClientAdmin, hasAccess } = useUserPermissions();

  // Manage client scoping with AdminContext
  const {
    selectedClient,
    setSelectedClient,
    isAppAdmin: isAppAdminContext,
    availableClients,
    loading: adminLoading,
    error: adminError
  } = useAdminContext();

  // Fetch user data
  const { users, loading: usersLoading, error: usersError, refetch } = useUserData(selectedClient);

  useEffect(() => {
    // Simulate initial load
    if (user) {
      setLoading(false);
    }
  }, [user]);

  // Handle create user
  const handleCreateUser = () => {
    setSelectedUser(null);
    setFormOpen(true);
  };

  // Handle edit user
  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setFormOpen(true);
  };

  // Handle form close
  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedUser(null);
  };

  // Handle form success
  const handleFormSuccess = () => {
    refetch();
  };

  // Handle deactivate user
  const handleDeactivateUser = (userToDeactivate) => {
    setUserToDeactivate(userToDeactivate);
    setDeactivateDialogOpen(true);
  };

  // Handle deactivate dialog close
  const handleDeactivateDialogClose = () => {
    setDeactivateDialogOpen(false);
    setUserToDeactivate(null);
  };

  // Handle deactivate success
  const handleDeactivateSuccess = () => {
    refetch();
  };

  // Permission denied
  if (!loading && !hasAccess) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            You do not have permission to access this page. Only APP_ADMIN and CLIENT_ADMIN users can manage users.
          </Alert>
        </Box>
      </DashboardLayout>
    );
  }

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isAppAdmin
              ? 'Manage users across all clients'
              : `Manage users for ${user?.client}`
            }
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Top Section: Single Row with Client Selector, Search Bar, Create User Button */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Client Selector (APP_ADMIN only) */}
            {isAppAdmin && (
              <Box sx={{ minWidth: 250, flex: '0 0 auto' }}>
                <ClientSelector
                  value={selectedClient}
                  onChange={setSelectedClient}
                  clients={availableClients}
                  loading={adminLoading}
                  error={adminError}
                />
              </Box>
            )}

            {/* Search Bar */}
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <TextField
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Search users..."
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                aria-label="Search users"
              />
            </Box>

            {/* Create User Button */}
            <Box sx={{ flex: '0 0 auto' }}>
              <AdminButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateUser}
                disabled={!selectedClient}
              >
                Create User
              </AdminButton>
            </Box>
          </Box>

          {/* Context indicator and alerts */}
          {selectedClient && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Showing users for: <strong>{selectedClient}</strong>
            </Typography>
          )}

          {!selectedClient && isAppAdmin && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Please select a client to view and manage users
            </Alert>
          )}

          {usersError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {usersError.message || 'Failed to load users'}
            </Alert>
          )}
        </Paper>

        {/* Bottom Section: User Table */}
        {selectedClient && (
          <Paper sx={{ p: 3 }}>
            <UserTable
              users={users}
              loading={usersLoading}
              onEdit={handleEditUser}
              onDeactivate={handleDeactivateUser}
              externalFilter={searchFilter}
            />
          </Paper>
        )}

        {/* User Form Dialog */}
        {selectedClient && (
          <UserForm
            open={formOpen}
            onClose={handleFormClose}
            onSuccess={handleFormSuccess}
            clientId={selectedClient}
            user={selectedUser}
            currentUser={user}
          />
        )}

        {/* Deactivate User Dialog */}
        <DeactivateUserDialog
          open={deactivateDialogOpen}
          onClose={handleDeactivateDialogClose}
          onSuccess={handleDeactivateSuccess}
          user={userToDeactivate}
          currentUser={user}
        />
      </Box>
    </DashboardLayout>
  );
}
